import React, { useEffect, useState } from "react";
import { Alert, Box, Button, TextField, Typography } from "@mui/material";
import { Formik, Form } from "formik";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useOutletContext } from "react-router-dom";
import ConfirmationDialog from "../../../../ConfirmationDialog";
import { FormPrompt } from "../../../../FormPrompt/FormPrompt";
import {
  createOwnershipTransfer,
  fetchUsersAndInvitations,
} from "../../../../../redux/features/organizationsSlice";
import { usersSliceSelector } from "../../../../../redux/features/usersSlice";

const TransferOwnership = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { organization } = useOutletContext();
  const usersState = useSelector((state) => usersSliceSelector(state));
  const currentEmail = usersState?.user?.email || "";
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const organizationId = organization?.id;

  useEffect(() => {
    if (organizationId) {
      dispatch(fetchUsersAndInvitations({ organizationId }));
    }
  }, [dispatch, organizationId]);

  if (!organization) {
    return null;
  }

  const pendingTransfers = (organization.ownershipTransfers || []).filter(
    (transfer) =>
      !transfer.declinedAt && !transfer.canceledAt && !transfer.acceptedAt,
  );

  const validationSchema = yup.object({
    email: yup
      .string()
      .trim()
      .email("Enter a valid email")
      .required("Email is required")
      .test(
        "not-self",
        "You cannot transfer ownership to yourself",
        (value) => value?.toLowerCase() !== currentEmail.toLowerCase(),
      ),
  });

  return (
    <Box>
      <Formik
        initialValues={{ email: "" }}
        validationSchema={validationSchema}
        onSubmit={async (values, { setStatus, resetForm }) => {
          resetForm({ values });
          const result = await dispatch(
            createOwnershipTransfer({
              organizationId: organization.id,
              values,
            }),
          );
          if (
            createOwnershipTransfer.fulfilled.match(result) &&
            result.payload?.transfer
          ) {
            navigate("../");
            return;
          }
          setStatus("Could not start the ownership transfer. Try again.");
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          dirty,
          handleReset,
          isSubmitting,
          submitForm,
          status,
        }) => (
          <Form noValidate autoComplete="off">
            <Box
              width={"100%"}
              display={"flex"}
              alignItems={"center"}
              gap={"20px"}
              marginTop={"40px"}
              marginBottom={"20px"}
            >
              <Typography variant="h2">Transfer Ownership</Typography>
              <Button
                onClick={() => {
                  if (dirty) {
                    setConfirmationDialogOpen(true);
                  } else {
                    navigate("../");
                  }
                }}
              >
                Close
              </Button>
            </Box>
            <Typography>
              Enter the email address of the new owner. They'll get an email and
              can accept the next time they log in. After they accept, you'll
              be removed from {organization.name}.
            </Typography>
            {pendingTransfers.length > 0 && (
              <Alert
                severity="warning"
                sx={{ marginTop: "20px" }}
                data-testid="pending-transfer-notice"
              >
                {pendingTransfers.length === 1
                  ? `A transfer to ${pendingTransfers[0].email} is already pending. Sending this form cancels that transfer and emails the address you enter instead.`
                  : "Another ownership transfer is already pending. Sending this form cancels it and emails the address you enter instead."}
              </Alert>
            )}
            <FormPrompt hasUnsavedChanges={dirty} />
            <ConfirmationDialog
              open={confirmationDialogOpen}
              title={"Close?"}
              message={
                "You have unsaved changes. Save or discard them to continue."
              }
              buttonDefsOverride={[
                {
                  text: "Cancel",
                  onClick: () => {
                    setConfirmationDialogOpen(false);
                  },
                },
                {
                  text: "Discard",
                  onClick: async () => {
                    await handleReset();
                    navigate("../");
                  },
                },
                {
                  text: "Save",
                  type: "submit",
                  onClick: () => {
                    setConfirmationDialogOpen(false);
                    submitForm();
                  },
                },
              ]}
            />
            <Box display={"flex"} gap={"20px"} alignItems={"center"}>
              <Box flexGrow={1}>
                <TextField
                  required
                  id="email"
                  name="email"
                  label="Email address"
                  sx={{ marginTop: "20px", width: "400px" }}
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={Boolean(touched.email && errors.email)}
                  helperText={
                    touched.email && errors.email ? errors.email : " "
                  }
                  inputProps={{ "data-testid": "ownership-transfer-email" }}
                />
              </Box>
              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting}
                data-testid="ownership-transfer-submit"
              >
                Send Transfer
              </Button>
            </Box>
            {status && (
              <Typography
                color="error"
                data-testid="ownership-transfer-error"
              >
                {status}
              </Typography>
            )}
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default TransferOwnership;
